// Form is based on Formik
// Data validation is based on Yup
// Please, be familiar with article first:
// https://hackernoon.com/react-form-validation-with-formik-and-yup-8b76bda62e10

import React, { useEffect, useRef, useState } from "react";
import { Formik, Form, Field, validateYupSchema, yupToFormErrors } from "formik";

import * as Yup from "yup";
import { yupNumber } from "../../../helpers/yupNumber";
import {
  Input,
  TextArea,
  Select,
  SelectCreatable,
  DatePickerField,
} from "../../../../_metronic/_partials/controls";
import moment from "moment";
import { useEventContext } from "./EventDataContext";
import { PersistFormikValues } from "formik-persist-values";

// Registration dates are compared as calendar days ("YYYY-MM-DD"), never as
// Date objects: Yup reads a typed "2026-12-31" as local midnight but a loaded
// End Date ("2026-12-31T00:00:00.000Z" from the UTC servers) as UTC midnight,
// so a Date comparison rejected Registration To = the event's last day.
// The date inputs give "YYYY-MM-DD" or "" (blank = not set).
const optionalDay = () =>
  Yup.string()
    .nullable()
    .matches(/^(\d{4}-\d{2}-\d{2})?$/, "Enter a valid date");

// End Date as a calendar day, read from the RAW form value — a typed
// "YYYY-MM-DD" or a loaded "2026-12-31T00:00:00.000Z" — never from Yup's cast
// Date (this.parent.enddate). Yup casts a typed date to the BROWSER's local
// midnight, so in a browser ahead of UTC (e.g. India) its UTC date is the day
// before, and Registration To = the last day would fail. The raw values come
// in as validation context (validateEventForm below).
const rawDay = (value) =>
  typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value) ? value.slice(0, 10) : null;

function notAfterEndDate(message) {
  return [
    "not-after-end-date",
    message,
    function (value) {
      const end = rawDay(this.options.context?.values?.enddate);
      return !value || !end || value <= end;
    },
  ];
}

// Validation schema
const EventEditSchema = Yup.object().shape({
  active_id: Yup.string(),
  fee: yupNumber()
    .min(0, "0 is minimum")
    .required("Fee is required"),
  image: Yup.object().shape({
      label: Yup.string().required(),
      value: Yup.string().required()
    })
    .required("An image URL is required")
    .nullable()
    // A missing image is the required check's to report (a new event starts
    // blank); this one only judges a link that is there.
    .test("valid-image", "Image URL is not valid", (image) => {
      if (!image || typeof image.value !== "string") return true;
      return image.value.match(/\.(jpeg|jpg|png|webp)$/) !== null;
    }),
  description: Yup.string()
    .required("Description is required"),
  max_students: yupNumber()
    .min(1, "1 is minimum")
    .required("A max participants number is required"),
  grade_min_nbr: yupNumber()
    .min(1, "1 is minimum")
    .max(13, "13 is maximum"),
  grade_max_nbr: yupNumber()
    .min(1, "1 is minimum")
    .max(13, "13 is maximum")
    .when('grade_min_nbr', {
      is: (grade_min_nbr => grade_min_nbr > 1),
      then: yupNumber()
        .min(Yup.ref("grade_min_nbr"), "Max Grade should be greater than or equal Min Grade")
    }),
  startdate: Yup.date()
    .required("Start Date is required"),
  enddate: Yup.date()
    .when('startdate', (startdate, schema) => {
      return startdate ?
      schema.min(startdate, "Start Date must be before End Date")
      : schema
    })
    .required("End Date is required"),
  // Optional. Blank From = registration open now; blank To = open until the
  // event ends. After To, the website shows the event as Full.
  // Neither may fall after the event's End Date.
  registration_start_date: optionalDay().test(
    ...notAfterEndDate("Registration From must be on or before the event's End Date")
  ),
  registration_end_date: optionalDay()
    .test(
      "not-before-start",
      "Registration To must be on or after Registration From",
      function (value) {
        const from = this.parent.registration_start_date;
        return !value || !from || value >= from;
      }
    )
    .test(...notAfterEndDate("Registration To must be on or before the event's End Date")),
    start_time: Yup.string(),
    end_time: Yup.string()
      .test("is-greater", "end time should be greater", function(value) {
        const { start_time } = this.parent;
        return typeof start_time !== "undefined" ? moment(value, "hh:mm").isAfter(moment(start_time, "hh:mm")) : true;
      }),
  price_subheader: Yup.string(),

  workshop_name: Yup.string().required("Event Name is required"),

  // Optional: a link replaces the built-in website checkout for this event.
  // A link typed without a scheme ("forms.gle/abc", "www.…") is checked as
  // https://…, which is how the website opens it (externalFormUrl). Only the
  // check sees the prefix; the value is saved as typed.
  form_link: Yup.string()
    .nullable()
    .trim()
    .transform((value) =>
      value && !/^[a-z][a-z0-9+.-]*:/i.test(value) ? `https://${value}` : value
    )
    .url("Enter a web address, e.g. https://forms.gle/…"),
  full_ind: Yup.string(),
  student_only_ind: Yup.string(),
  emergency_contact_ind: Yup.string(),
  fee_tax_ind: Yup.mixed(),
  event_type_id: Yup.mixed().required("Event Type is required"),
});

// What `validationSchema={EventEditSchema}` did, plus the raw form values as
// Yup context so the registration-date checks compare calendar days rather
// than browser-local Dates (see rawDay above).
export const validateEventForm = (values) =>
  validateYupSchema(values, EventEditSchema, false, { values }).then(
    () => ({}),
    (err) => {
      if (err && err.name === "ValidationError") return yupToFormErrors(err);
      throw err;
    }
  );

// Calls onChange with a Formik value on mount and whenever it changes, so the
// page can follow a field before it is saved (the Registration questions tab
// follows the Event Type choice).
function ValueWatcher({ value, onChange }) {
  useEffect(() => {
    onChange && onChange(value);
  }, [value, onChange]);
  return null;
}

// Calls onInvalid with Formik's errors when a submit ends without saving
// because validation failed (isSubmitting true -> false with errors).
function InvalidSubmitReporter({ isSubmitting, errors, onInvalid }) {
  const wasSubmitting = useRef(false);
  useEffect(() => {
    if (wasSubmitting.current && !isSubmitting && Object.keys(errors).length && onInvalid) {
      onInvalid(errors);
    }
    wasSubmitting.current = isSubmitting;
  }, [isSubmitting, errors, onInvalid]);
  return null;
}

// eventTypes: GET /api/workshops/event-types results, null while loading.
// showEventType: show the Event Type select (EventEdit always does, for all
// event staff); hidden, the event's type is left as it is.
// onTypeChange: called with the chosen event_type_id, saved or not.
// onInvalidSubmit: called with the errors when Save is refused by validation.
export function EventEditForm({
  event,
  btnRef,
  saveEvent,
  defaultImages,
  eventTypes,
  showEventType,
  onTypeChange,
  onInvalidSubmit,
}) {
  const DROPDOWN_TYPE = useEventContext().TYPES;
  // A new event starts with no image: the dropdown offers the default images,
  // and a pasted link is added with the field's "Create" option. An existing
  // event shows its own image, with no defaults on offer.
  const images = event.workshop_id ? [] : defaultImages;
  // The field and its preview read Formik's own `values.image`, never a copy:
  // a copy kept the old pick on screen after New Event was rebuilt for
  // another branch (EventEdit's setValues), while Save saw no image.
  // The link whose preview failed to load; a different link starts clean.
  const [failedImageLink, setFailedImageLink] = useState(null);
  // The first row's three short selects narrow to make room for Event Type,
  // so the row stays one line: 3+3+3+3, or 2+2+2+3+3 with the type.
  const shortCol = showEventType ? "col-md-2" : "col-md-3";


  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={event}
        validate={validateEventForm}
        // Returned, so Formik clears isSubmitting when the save settles (the
        // invalid-submit report above relies on it).
        onSubmit={(values) => saveEvent(values)}
      >
        {({ handleSubmit, values, errors, setFieldValue, isSubmitting }) => (
          <>
            <Form className="form form-label-right">
              <div className="form-group row">
                <div className={shortCol}>
                  <Select name={`active_id`} label="Event Active">
                    {DROPDOWN_TYPE.ACTIVE &&
                      DROPDOWN_TYPE.ACTIVE.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.value}
                        </option>
                      ))}
                  </Select>
                </div>
                <div className={shortCol}>
                  <Select name="full_ind" label="Event Full?">
                    <option key={0} value={0}>
                      {" "}
                      Not Full
                    </option>
                    <option key={1} value={1}>
                      {" "}
                      Full
                    </option>
                  </Select>
                </div>
                <div className={shortCol}>
                  <Select name="student_only_ind" label="Target Audience">
                    <option key={0} value={0}>
                      Parent/Tutor
                    </option>
                    <option key={1} value={1}>
                      Student Only
                    </option>
                  </Select>
                </div>
                <div className="col-md-3">
                  <Select name="emergency_contact_ind" label="Collect emergency contact">
                    <option key={1} value={1}>
                      Yes
                    </option>
                    <option key={0} value={0}>
                      No
                    </option>
                  </Select>
                  <small className="form-text text-muted">
                    The website asks the registering parent for an emergency
                    contact.
                  </small>
                </div>
                {/* The type's flags (custom_fields_ind, schedule_ind),
                    not its name, decide which tabs the event has. */}
                {showEventType && (
                  <div className="col-md-3">
                    <Select
                      name="event_type_id"
                      label="Event Type"
                      labelClasses="required-field"
                      options={(eventTypes || []).map((t) => ({
                        value: t.event_type_id,
                        label: t.event_type_name,
                      }))}
                    />
                  </div>
                )}
              </div>
              {/* {console.log(errors)} */}
              <div className="form-group row">
                <div className="col-lg-6">
                  <label className={"required-field"}>
                    Enter Event Name
                  </label>
                  <Field
                    name="workshop_name"
                    component={Input}
                    placeholder="Event Name"
                    type="text"
                  />
                </div>
                <div className="col-lg-3">
                  <label className={"required-field"}>Enter Event Fee</label>
                  <Field
                    name="fee"
                    component={Input}
                    placeholder="Fee"
                    type="number"
                  />
                  {/* Taxable event (fee_tax_ind, stored as 1/0): the website
                      checkout and Record Payment add the branch's tax only
                      when ticked. New events default by branch country
                      (EventEdit: Canada ticked, elsewhere not). */}
                  {/* Metronic hides the native input inside .checkbox and
                      draws the box with the <span> after it — without the
                      span the checkbox is invisible. Same markup as
                      "Available for Trials" (BranchCourseFeeEditForm). */}
                  <div className="d-flex align-items-center mt-3">
                    <label className="checkbox checkbox-lg mb-0">
                      <input
                        id="event-fee-tax-ind"
                        type="checkbox"
                        name="fee_tax_ind"
                        checked={Number(values.fee_tax_ind) === 1}
                        onChange={(e) => setFieldValue("fee_tax_ind", e.target.checked ? 1 : 0)}
                      />
                      <span></span>
                    </label>
                    {/* A second label for the same input: clicking the words
                        toggles the box too, and it names the box for screen
                        readers. Not a <span> inside .checkbox — Metronic
                        styles every such span as the box itself. */}
                    <label
                      htmlFor="event-fee-tax-ind"
                      className="ml-3 mb-0"
                      style={{ cursor: "pointer" }}
                    >
                      Taxable event — adds the branch&apos;s tax at checkout
                    </label>
                  </div>
                </div>
                <div className="col-lg-3">
                  <label>Enter Event Price Sub Header</label>
                  <Field
                    name="price_subheader"
                    component={Input}
                    placeholder="price_subheader"
                    type="text"
                  />
                </div>
              </div>
              <div className="form-group row">
                <div className={"col-lg-6"}>
                  <label>Registration From</label>
                  <Field
                    name="registration_start_date"
                    component={Input}
                    type="date"
                  />
                  <small className="form-text text-muted">
                    Optional. Blank means registration is open now.
                  </small>
                </div>
                <div className={"col-lg-6"}>
                  <label>Registration To</label>
                  <Field
                    name="registration_end_date"
                    component={Input}
                    type="date"
                  />
                  <small className="form-text text-muted">
                    Optional. Blank means open until the event ends. After
                    this date the website shows the event as Full.
                  </small>
                </div>
              </div>
              <div className="form-group row">
                <div className={"col-lg-6"}>
                  <label className={"required-field"}>Event Start Date</label>
                  <Field
                    name="startdate"
                    component={Input}
                    type="date"
                    value={values.startdate.split("T")[0]}
                  />
                </div>
                <div className={"col-lg-6"}>
                  <label className={"required-field"}>Event End Date</label>
                  <Field
                    name="enddate"
                    component={Input}
                    type="date"
                    value={values.enddate.split("T")[0]}
                  />
                </div>
              </div>
              <div className="form-group row">
                <div className={"col-lg-6"}>
                  <Field
                    name="start_time"
                    component={Input}
                    type="time"
                    placeholder="Start Time"
                    label="Start Time"
                  />
                </div>
                <div className={"col-lg-6"}>
                  <Field
                    name="end_time"
                    component={Input}
                    type="time"
                    placeholder="End Time"
                    label="End Time"
                  />
                </div>
              </div>
              <div className="form-group row">
                <div className="col-lg-6">
                  <label className={"required-field"}>Event Image</label>
                  <SelectCreatable
                    name="image"
                    options={images}
                    isClearable
                    placeholder="Paste an image link (.jpg, .png or .webp), or choose a default"
                    // null, not undefined: react-select treats undefined as
                    // uncontrolled and would keep showing its last pick.
                    value={values.image || null}
                    onChange={(newSelectedImage) => {
                      setFieldValue("image", newSelectedImage || null);
                    }}
                  />
                  {/* The chosen image, so staff can see the link works before
                      saving. */}
                  {values.image?.value &&
                    (failedImageLink === values.image.value ? (
                      <div className="form-text text-danger">
                        Image could not be loaded
                      </div>
                    ) : (
                      <img
                        // keyed by the link, so a new link loads afresh
                        key={values.image.value}
                        src={values.image.value}
                        alt="Preview of the chosen event artwork"
                        onError={() => setFailedImageLink(values.image.value)}
                        className="mt-3 rounded border"
                        style={{ maxHeight: 120, maxWidth: "100%" }}
                      />
                    ))}
                </div>
                <div className="col-lg-6">
                  <label>Enter Event Form Link</label>
                  <Field
                    name="form_link"
                    component={Input}
                    placeholder="Form link"
                    type="text"
                  />
                  {values.form_link?.trim() && (
                    <div className="alert alert-custom alert-light-warning mt-3 mb-0">
                      <div className="alert-text">
                        This link will replace the default system registration
                        flow. On the website, Register will open this form in a
                        new tab instead of the built-in checkout.
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="form-group row">
              <div className="col-lg-6">
                  <label>Enter Min Grade</label>
                  <Field
                    name="grade_min_nbr"
                    component={Input}
                    placeholder="1"
                    type="number"
                  />
                </div>
                <div className="col-lg-6">
                  <label>Enter Max Grade</label>
                  <Field
                    name="grade_max_nbr"
                    component={Input}
                    placeholder="12"
                    type="number"
                  />
                </div>
              </div>
              <div className="form-group row">
                <div className="col-lg-6">
                  <label className={"required-field"}>Enter Max Participants</label>
                  <Field
                    name="max_students"
                    component={Input}
                    placeholder="0"
                    type="number"
                  />
                </div>
              </div>
              <div className="form-group row">
                <div className="col-lg-12">
                  <label className={"required-field"}>
                    Enter Event Description
                  </label>
                  <Field
                    name="description"
                    placeholder="Enter Event Description"
                    component={TextArea}
                    rows="5"
                    cols="50"
                  />
                </div>
              </div>
              <button
                type="submit"
                style={{ display: "none" }}
                ref={btnRef}
                onSubmit={() => handleSubmit()}
              ></button>
              {/* {event.workshop_id && <PersistFormikValues name="event-form" />} */}
            </Form>
            <ValueWatcher value={values.event_type_id} onChange={onTypeChange} />
            <InvalidSubmitReporter
              isSubmitting={isSubmitting}
              errors={errors}
              onInvalid={onInvalidSubmit}
            />
          </>
        )}
      </Formik>
    </>
  );
}
